import { Pagination, PaginationV2 } from "@/domain/entities/Pagination";
import {
  UrgentDeliveryManagementPODData,
  UrgentDeliveryManagementPODList,
  UrgentDeliveryManagementRTSData,
  UrgentDeliveryManagementRTSList,
  UrgentDeliveryManagementHALData,
  UrgentDeliveryManagementHALList,
  UrgentDeliveryManagementTakenByRecipientList,
  UrgentDeliveryManagementTakenByRecipient,
  UrgentDeliveryManagementDetailData,
  UrgentDeliveryManagementPODSummaryData,
  UrgentDeliveryManagementRelabelList,
  UrgentDeliveryManagementRelabelData,
  LastLocationOption
} from "@/domain/entities/UrgentDeliveryManagement";
import { AxiosResponse } from "axios";
import { ResponsePayloadV2 } from '@/domain/entities/ResponsePayload';
import { convertSnakeToCamel } from '@/app/infrastructures/misc/common-library/ConvertSnakeToCamel';

export class UrgentDeliveryManagementMapper {
  convertPODListFromApi(resp: AxiosResponse): UrgentDeliveryManagementPODList {
    const {
      data: { data, meta }
    } = resp;

    return new UrgentDeliveryManagementPODList(
      data.map((pod: Record<string, string | number>) => {
        return new UrgentDeliveryManagementPODData({
          pdId: pod.pd_id as number,
          pdSTTNo: pod.pd_stt_no as string,
          pdFlag: pod.pd_flag as string,
          sttDeliveryType: pod.stt_delivery_type as string,
          pdReason: pod.pd_reason as string,
          pdDeadlineReturn: pod.pd_deadline_return as string,
          sttLastReturnId: pod.stt_last_status_id as string,
          sttDestinationCity: pod.stt_destination_city as string,
          sttRecipientName: pod.stt_recipient_name as string,
          sttRecipientPhone: pod.stt_recipient_phone as string,
          sttDestinationCityName: pod.stt_destination_city_name as string,
          sttDestinationDistrictName: pod.stt_destination_district_name as string,
          deliveryDEXAttempt: pod.delivery_dex_attempt as number,
          pdCreatedAt: pod.pd_created_at as string,
          pdUpdatedAt: pod.pd_updated_at as string,
          pdProductType: pod.pd_product_type as string,
          pdCreatedName: pod.pd_created_name as string,
          pdCreatedRoleName: pod.pd_created_role_name as string,
          sttBookedAt: pod.stt_booked_at as string,
          sttBookedName: pod.stt_booked_name as string,
          pdAgingDays: pod.pd_aging_days as number,
          cityCode: (pod.city_code as string) || "",
          hubId: (pod.hub_id as number) ?? 0,
          hubName: (pod.hub_name as string) || "",
          stationBucket: (pod.station_bucket as string) || "",
          lastLocationLabel: (pod.last_location_label as string) || ""
        });
      }),
      new Pagination(meta.page, meta.limit, meta.total_records)
    );
  }

  convertLastLocationOptionsFromApi(resp: AxiosResponse): LastLocationOption[] {
    const { data } = resp.data;
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map(
      (row: Record<string, string | number | null | undefined>) => ({
        name: String(row.label || ""),
        value: {
          cityCode: String(row.city_code || ""),
          hubId: row.hub_id ?? 0
        }
      })
    ) as LastLocationOption[];
  }

  convertRTSListFromApi(resp: AxiosResponse): UrgentDeliveryManagementRTSList {
    const {
      data: { data, meta }
    } = resp;

    return new UrgentDeliveryManagementRTSList(
      data.map((rts: Record<string, string | number>) => {
        return new UrgentDeliveryManagementRTSData({
          pdId: rts.pd_id as number,
          pdSTTNo: rts.pd_stt_no as string,
          pdFlag: rts.pd_flag as string,
          sttDeliveryType: rts.stt_delivery_type as string,
          pdCaseType: rts.pd_case_type as string,
          pdReason: rts.pd_reason as string,
          pdDeadlineReturn: rts.pd_deadline_return as string,
          sttLastStatusId: rts.stt_last_status_id as string,
          sttOriginCityId: rts.stt_origin_city_id as string,
          sttOriginCityName: rts.stt_origin_city_name as string,
          sttDestinationCityId: rts.stt_destination_city_id as string,
          sttDestinationCityName: rts.stt_destination_city_name as string,
          pdCreatedAt: rts.pd_created_at as string,
          pdUpdatedAt: rts.pd_updated_at as string,
          pdProductType: rts.pd_product_type as string,
          pdCreatedName: rts.pd_created_name as string,
          pdCreatedRoleName: rts.pd_created_role_name as string,
          pdDexCustomerAttempt: rts.pd_dex_customer_attempt as number,
          pdBkdDate: rts.stt_booked_at as string,
          pdBkdByName: rts.stt_booked_name as string,
          pdBkdByRole: rts.stt_booked_role_name as string,
          pdFlaggedDate: rts.pd_created_at as string,
          pdFlaggedByName: rts.pd_created_name as string,
          pdFlaggedByRole: rts.pd_created_role_name as string,
          sttClientAggregatorType: rts.stt_client_aggregator_type as string,
          cityCode: (rts.city_code as string) || "",
          hubId: Number(rts.hub_id) || 0,
          hubName: (rts.hub_name as string) || "",
          stationBucket: (rts.station_bucket as string) || "",
          lastLocationLabel: (rts.last_location_label as string) || ""
        });
      }),
      new Pagination(meta.page, meta.limit, meta.total_records)
    );
  }

  convertHALListFromApi(resp: AxiosResponse): UrgentDeliveryManagementHALList {
    const {
      data: { data, meta }
    } = resp;

    return new UrgentDeliveryManagementHALList(
      data.map((hal: Record<string, string | number>) => {
        return new UrgentDeliveryManagementHALData({
          pdId: hal.pd_id as number,
          pdSTTNo: hal.pd_stt_no as string,
          pdFlag: hal.pd_flag as string,
          sttDeliveryType: hal.stt_delivery_type as string,
          pdCaseType: hal.pd_case_type as string,
          pdReason: hal.pd_reason as string,
          pdDeadlineReturn: hal.pd_deadline_return as string,
          sttLastStatusId: hal.stt_last_status_id as string,
          sttOriginCityId: hal.stt_origin_city_id as string,
          sttOriginCityName: hal.stt_origin_city_name as string,
          sttDestinationCityId: hal.stt_destination_city_id as string,
          sttDestinationCityName: hal.stt_destination_city_name as string,
          pdCreatedAt: hal.pd_created_at as string,
          pdUpdatedAt: hal.pd_updated_at as string,
          createdBy: hal.created_by as string,
          cityCode: (hal.city_code as string) || "",
          hubId: (hal.hub_id as number) ?? 0,
          hubName: (hal.hub_name as string) || "",
          stationBucket: (hal.station_bucket as string) || "",
          lastLocationLabel: (hal.last_location_label as string) || ""
        });
      }),
      new Pagination(meta.page, meta.limit, meta.total_records)
    );
  }

  convertListTakenByRecipient(resp: AxiosResponse<any>): UrgentDeliveryManagementTakenByRecipientList {
    const { data: { data, meta } } = resp;
    return new UrgentDeliveryManagementTakenByRecipientList({
      pagination: new PaginationV2({ page: meta.page, limit: meta.limit, totalData: meta.total_records }),
      data: !data.length ? [] : data.map((key: any) => new UrgentDeliveryManagementTakenByRecipient({
        pdId: key.pd_id,
        pdSttNo: key.pd_stt_no,
        sttProductType: key.stt_product_type,
        sttId: key.stt_id,
        sttIsCod: key.stt_is_cod,
        sttIsDfod: key.stt_is_dfod,
        sttLastStatusId: key.stt_last_status_id,
        sttDestinationCity: key.stt_destination_city,
        sttRecipientName: key.stt_recipient_name,
        sttRecipientPhone: key.stt_recipient_phone,
        sttDestinationCityName: key.stt_destination_city_name,
        sttDestinationDistrictName: key.stt_destination_district_name,
        pdCreatedAt: key.pd_created_at,
        pdUpdatedAt: key.pd_updated_at,
      }))
    })

  }

  public convertDeliveryManagementDetailDataFromApi(
    res: AxiosResponse<any>
  ): UrgentDeliveryManagementDetailData {
    const { data } = res.data;
    const limitDate = data.pd_deadline_return?.split("-")
    return new UrgentDeliveryManagementDetailData({
      pdId: data.pd_id,
      pdSttNo: data.pd_stt_no,
      rpdRequestType: data.rpd_request_type,
      pdReason: data.pd_reason,
      pdDeadlineReturn: limitDate[0] === "0001" ? "-" : data.pd_deadline_return,
      sttLastStatusId: data.stt_last_status_id,
      sttDestinationCity: data.stt_destination_city,
      rpdNotes: data.rpd_notes,
      rpdProofPhoto: data.rpd_proof_photo,
      pdCreatedAt: data.pd_created_at,
      pdCreatedBy: data.pd_created_name,
      pdCreatedRoleName: data.pd_created_role_name,
      rpdLink: data.rpd_link,
      pdBkdDate: data.stt_booked_at,
      pdBkdByName: data.stt_booked_name
    });
  }

  public convertDeliveryManagementPODSummary(
    res: AxiosResponse<any>
  ): UrgentDeliveryManagementPODSummaryData {
    const { data } = res.data;
    return new UrgentDeliveryManagementPODSummaryData({
      prioritShipment: data.priority_shipment,
      overSlaLimit: data.over_sla_limit,
      redelivery: data.redelivery,
      noFlag: data.no_flag,
    });
  }

  convertRelabelListFromApi(
    resp: AxiosResponse
  ): UrgentDeliveryManagementRelabelList {
    const {
      data: { data, meta }
    } = resp;

    return new UrgentDeliveryManagementRelabelList({
      pagination: new PaginationV2({
        page: meta.page,
        limit: meta.limit,
        totalData:
          typeof meta.total_records === "string" && meta.total_records.trim()
            ? Number(meta.total_records)
            : meta.total_records
      }),
      data: !Array.isArray(data) || !data.length
        ? []
        : data.map(
            (relabel: Record<string, string | number>) =>
              new UrgentDeliveryManagementRelabelData({
                pdId: relabel.pd_id as number,
                pdSttNo: relabel.pd_stt_no as string,
                rootReverseSttNo: relabel.root_reverse_stt_no as string,
                processedBy: relabel.processed_by as string,
                productType: relabel.product_type as string,
                overtimeDurationDays: relabel.overtime_duration_days as number,
                bkdAt: relabel.bkd_at as string,
                originHubName: (relabel.origin_hub_name as string) || "",
                originCityCode: (relabel.origin_city_code as string) || "",
                destinationCityCode: (relabel.destination_city_code as string) || "",
                originCityName: relabel.origin_city_name as string,
                destinationCityName: relabel.destination_city_name as string
              })
          )
    });
  }

  public convertListUrgentDeliveryManagementUrgentSearchDataFromApi(
    res: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const { data, meta } = res.data;
    return new ResponsePayloadV2(
      convertSnakeToCamel({
        pagination: new PaginationV2({
          ...meta,
          totalData: meta.total
        }),
        data
      })
    );
  }
}
